"use strict";

import numeric from 'numeric';

/* AHP Rank Scale Table */
const AHP_RANK_SCALE_TABLE = [
  {
    scale: 1,
    definition: "Equal importance",
    explanation: "Two factors contribute equally to the objective.",
  },
  {
    scale: 3,
    definition: "Somewhat more importance",
    explanation: "Experience and judgment slightly favor one over the other.",
  },
  {
    scale: 5,
    definition: "Much more important",
    explanation: "Experience and judgment strongly favor one over the other.",
  },
  {
    scale: 7,
    definition: "Very much more important",
    explanation:
      "Experience and judgment very strongly favor one over the other. Its importance is demonstrated in practice.",
  },
  {
    scale: 9,
    definition: "Absolutely more important",
    explanation:
      "The evidence favoring one over the other is of the highest possible validity.",
  },
];

/**
 * Class AHP
 *
 * @class AHP
 */
function AHP(context) {
  this.items = [];
  this.criteria = [];
  this.criteriaItemRank = {}; // for each criterion, compare between items
  this.criteriaRank = [];

  if (context) {
    this.import(context);
  }
}

AHP.prototype.import = function (context) {
  let self = this;
  if (context.items && context.items.length > 0) {
    self.addItems(context.items);
  }
  if (context.criteria && context.criteria.length > 0) {
    self.addCriteria(context.criteria);
  }
  if (context.criteriaItemRank) {
    for (const criterion in context.criteriaItemRank) {
      self.criteriaItemRank[criterion] = self.criteriaItemRank[criterion] || [];

      const criterionItemRankContext = context.criteriaItemRank[criterion];

      if (
        Array.isArray(criterionItemRankContext) &&
        criterionItemRankContext[0] &&
        !Array.isArray(criterionItemRankContext[0])
      ) {
        const scoreVector = criterionItemRankContext;
        for (let i = 0; i < scoreVector.length; i++) {
          for (let j = 0; j < scoreVector.length; j++) {
            self.criteriaItemRank[criterion][i][j] =
              scoreVector[i] / scoreVector[j];
            if (isNaN(self.criteriaItemRank[criterion][i][j])) {
              self.criteriaItemRank[criterion][i][j] = null;
            }
          }
        }
      } else if (
        Array.isArray(criterionItemRankContext) &&
        criterionItemRankContext[0] &&
        Array.isArray(criterionItemRankContext[0])
      ) {
        for (let i = 0; i < criterionItemRankContext.length; i++) {
          self.criteriaItemRank[criterion][i] =
            criterionItemRankContext[i] || [];
          for (let j = 0; j < criterionItemRankContext[i].length; j++) {
            self.criteriaItemRank[criterion][i][j] =
              criterionItemRankContext[i][j];
            if (isNaN(self.criteriaItemRank[criterion][i][j])) {
              self.criteriaItemRank[criterion][i][j] = null;
            }
          }
        }
      }
    }
  }

  if (context.criteriaRank) {
    let criteriaRankContext = context.criteriaRank;
    if (
      Array.isArray(criteriaRankContext) &&
      criteriaRankContext[0] &&
      !Array.isArray(criteriaRankContext[0])
    ) {
      let scoreVector = criteriaRankContext;
      for (let i = 0; i < scoreVector.length; i++) {
        for (let j = 0; j < scoreVector.length; j++) {
          self.criteriaRank[i][j] = scoreVector[i] / scoreVector[j];
          if (isNaN(self.criteriaRank[i][j])) {
            self.criteriaRank[i][j] = null;
          }
        }
      }
    } else if (
      Array.isArray(criteriaRankContext) &&
      criteriaRankContext[0] &&
      Array.isArray(criteriaRankContext[0])
    ) {
      for (var i = 0; i < criteriaRankContext.length; i++) {
        self.criteriaRank[i] = self.criteriaRank[i] || [];
        for (var j = 0; j < criteriaRankContext[i].length; j++) {
          self.criteriaRank[i][j] = criteriaRankContext[i][j];
          if (isNaN(self.criteriaRank[i][j])) {
            self.criteriaRank[i][j] = null;
          }
        }
      }
    }
  }
  return self;
};

AHP.prototype.export = function () {
  let self = this;
  let json = {
    items: [],
    criteria: [],
    criteriaItemRank: {},
    criteriaRank: [],
  };
  json.items.push.apply(json.items, self.items);
  json.criteria.push.apply(json.criteria, self.criteria);
  json.criteriaItemRank = JSON.parse(JSON.stringify(self.criteriaItemRank));
  json.criteriaRank = JSON.parse(JSON.stringify(self.criteriaRank));
  return json;
};

AHP.prototype.addItems = function (items) {
  let self = this;
  items = items.filter((item) => self.items.indexOf(item) === -1);

  let originalLength = self.items.length;
  self.items.push.apply(self.items, items);

  for (let criterion of self.criteria) {
    for (let row of self.criteriaItemRank[criterion]) {
      row.push.apply(row, Array(items.length).fill(0));
    }
    items.forEach((item, i) => {
      let newRow = Array(self.items.length).fill(0);
      newRow[originalLength + i] = 1;
      self.criteriaItemRank[criterion].push(newRow);
    });
  }

  return self;
};

AHP.prototype.addItem = function (item) {
  this.addItems([item]);
  return this;
};

AHP.prototype.removeItem = function (item) {
  let self = this;
  let index = self.items.indexOf(item);
  if (index >= 0) {
    self.items.splice(index, 1);

    for (let criterion of self.criteria) {
      self.criteriaItemRank[criterion].splice(index, 1);
      for (let row of self.criteriaItemRank[criterion]) {
        row.splice(index, 1);
      }
    }
  }
  return self;
};

AHP.prototype.removeItems = function (items) {
  let self = this;
  items.forEach((item) => self.removeItem(item));
  return self;
};

AHP.prototype.addCriteria = function (criteria) {
  var self = this;
  criteria = criteria.filter(
    (criterion) => self.criteria.indexOf(criterion) === -1
  );

  let originalLength = self.criteria.length;
  self.criteria.push.apply(self.criteria, criteria);

  for (let criterion of self.criteria) {
    if (!self.criteriaItemRank[criterion]) {
      self.criteriaItemRank[criterion] = numeric.identity(self.items.length);
    }
  }

  for (let row of self.criteriaRank) {
    row.push.apply(row, Array(criteria.length).fill(0));
  }
  criteria.forEach((criterion, i) => {
    let newRow = Array(self.criteria.length).fill(0);
    newRow[originalLength + i] = 1;
    self.criteriaRank.push(newRow);
  });

  return self;
};

AHP.prototype.addCriterion = function (criterion) {
  return this.addCriteria([criterion]);
};

AHP.prototype.removeCriterion = function (criterion) {
  let self = this;
  let index = self.criteria.indexOf(criterion);
  if (index >= 0) {
    self.criteria.splice(index, 1);

    delete self.criteriaItemRank[criterion];

    self.criteriaRank.splice(index, 1);
    for (let row of self.criteriaRank) {
      row.splice(index, 1);
    }
  }
  return self;
};

AHP.prototype.removeCriteria = function (criteria) {
  let self = this;
  criteria.forEach((criterion) => self.removeCriterion(criterion));
  return self;
};

AHP.prototype.resetCriteriaItemRank = function (criteria) {
  let self = this;
  criteria.forEach(function (criterion) {
    this.criteriaItemRank[criterion] = numeric.identity(self.items.length);
  }, this);
  return self;
};

AHP.prototype.resetCriteriaRank = function () {
  let self = this;
  this.criteriaRank = numeric.identity(self.criteria.length);
  return self;
};

AHP.prototype.setCriteriaItemRankByGivenScores = function (criterion, scoreVector) {
  let self = this;
  for (let i = 0; i < scoreVector.length; i++) {
    for (let j = 0; j < scoreVector.length; j++) {
      self.criteriaItemRank[criterion][i][j] =
        scoreVector[i] / scoreVector[j];
    }
  }
  return self;
};

AHP.prototype.rankCriteriaItem = function (criterion, preferences) {
  let self = this;
  if (self.criteria.indexOf(criterion) === -1) {
    return self;
  }

  for (let prefer of preferences) {
    let preferredItem, comparingItem, scale;
    if (Array.isArray(prefer)) {
      [preferredItem, comparingItem, scale] = prefer;
    } else {
      preferredItem = prefer.preferredItem;
      comparingItem = prefer.comparingItem;
      scale = prefer.scale;
    }
    let itemAIndex = self.items.indexOf(preferredItem);
    let itemBIndex = self.items.indexOf(comparingItem);
    if (itemAIndex >= 0 && itemBIndex >= 0) {
      self.criteriaItemRank[criterion][itemAIndex][itemBIndex] = scale;
      self.criteriaItemRank[criterion][itemBIndex][itemAIndex] = 1 / scale;
    }
  }
  return self;
};

AHP.prototype.setCriteriaRankByGivenScores = function (scoreVector) {
  let self = this;
  for (let i = 0; i < scoreVector.length; i++) {
    for (let j = 0; j < scoreVector.length; j++) {
      self.criteriaRank[i][j] = scoreVector[i] / scoreVector[j];
    }
  }
  return self;
};

AHP.prototype.rankCriteria = function (preferences) {
  let self = this;

  for (let prefer of preferences) {
    let preferredCriterion, comparingCriterion, scale;
    if (Array.isArray(prefer)) {
      [preferredCriterion, comparingCriterion, scale] = prefer;
    } else {
      preferredCriterion = prefer.preferredCriterion;
      comparingCriterion = prefer.comparingCriterion;
      scale = prefer.scale;
    }
    let criterionAIndex = self.criteria.indexOf(preferredCriterion);
    let criterionBIndex = self.criteria.indexOf(comparingCriterion);
    if (criterionAIndex >= 0 && criterionBIndex >= 0) {
      self.criteriaRank[criterionAIndex][criterionBIndex] = scale;
      self.criteriaRank[criterionBIndex][criterionAIndex] = 1 / scale;
    }
  }
  return self;
};

/**
 * Run the AHP process to get the ranking and scores based on the AHP method.
 *
 * @returns {object} - Contains the ranking matrix, criteria weight vector, and ranked score map.
 *
 * @memberOf AHP
 */
AHP.prototype.run = function () {
  let self = this;

  // Matrix to store the ranking results for each criterion
  let rankingMatrix = Array(self.items.length)
    .fill(null)
    .map(() => Array(self.criteria.length).fill(0));

  // Criteria weights (assuming equal weights if none provided)
  let critWeightVector = Array(self.criteria.length).fill(1 / self.criteria.length);

  // Iterate through each criterion and calculate the normalized matrix and priority vector
  for (let j = 0; j < self.criteria.length; j++) {
    let criterion = self.criteria[j];
    let comparisonMatrix = self.criteriaItemRank[criterion];

    // Step 1: Normalize the comparison matrix column-wise
    let columnSums = Array(self.items.length).fill(0);
    for (let i = 0; i < self.items.length; i++) {
      for (let k = 0; k < self.items.length; k++) {
        columnSums[i] += comparisonMatrix[k][i];
      }
    }

    let normalizedMatrix = Array(self.items.length)
      .fill(null)
      .map(() => Array(self.items.length).fill(0));

    for (let i = 0; i < self.items.length; i++) {
      for (let k = 0; k < self.items.length; k++) {
        normalizedMatrix[i][k] = comparisonMatrix[i][k] / columnSums[k];
      }
    }

    // Step 2: Calculate the priority vector (row-wise average of the normalized matrix)
    let priorityVector = Array(self.items.length).fill(0);
    for (let i = 0; i < self.items.length; i++) {
      priorityVector[i] = normalizedMatrix[i].reduce((a, b) => a + b, 0) / self.items.length;
    }

    // Step 3: Store the priority vector in the ranking matrix
    for (let i = 0; i < priorityVector.length; i++) {
      rankingMatrix[i][j] = priorityVector[i];
    }
  }

  // Step 4: Synthesize the final scores for each item by multiplying rankingMatrix with criteria weights
  let rankedScores = numeric.dotMV(rankingMatrix, critWeightVector);
  let rankedScoreMap = {};
  self.items.forEach((item, i) => {
    rankedScoreMap[item] = rankedScores[i];
  });

  return {
    rankingMatrix,
    critWeightVector,
    rankedScoreMap,
    rankedScores,
  };
};

// Use ES6 export
export default AHP;
